import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { MzicSkeletonComponent, MzicSvgComponent } from '@mzic/mzic-components';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  AuthInteraction,
  AuthInteractionPageItem,
} from '../../../../interfaces/collection.interface';
import { AuthInteractionDraftState } from '../../../../services/auth-interaction-persistence.service';
import { AuthInteractionRendererState } from '../two-fa-code-interaction/two-fa-code-interaction.component';

@Component({
  selector: 'lib-scope-selection-interaction',
  imports: [
    CommonModule,
    TranslocoModule,
    MzicSkeletonComponent,
    MzicSvgComponent,
  ],
  templateUrl: './scope-selection-interaction.component.html',
  styleUrl: './scope-selection-interaction.component.scss',
})
export class ScopeSelectionInteractionComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input({ required: true }) authInteraction!: AuthInteraction;
  @Input() loading = false;
  @Input() lazyLoading = false;
  @Input() initialDraftState: AuthInteractionDraftState | null = null;

  @Output() rendererStateChange =
    new EventEmitter<AuthInteractionRendererState & { draftState: AuthInteractionDraftState }>();
  @Output() pageRequested = new EventEmitter<{ page: number; size: number }>();

  readonly selectedIds = signal<string[]>([]);
  readonly loadedItems = signal<AuthInteractionPageItem[]>([]);
  readonly shimmerItems = [1, 2, 3];

  private readonly loadMoreSubject = new Subject<void>();
  private readonly destroySubject = new Subject<void>();
  private observer: IntersectionObserver | null = null;
  private observerTarget: ElementRef<HTMLElement> | null = null;

  @ViewChild('observerTarget')
  set lazyObserverTarget(target: ElementRef<HTMLElement> | undefined) {
    this.observerTarget = target ?? null;
    this.initIntersectionObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialDraftState']) {
      this.selectedIds.set(this.initialDraftState?.selected_ids ?? []);
    }

    if (changes['authInteraction']) {
      this.syncLoadedItems();
    }

    if (
      changes['initialDraftState'] ||
      changes['authInteraction'] ||
      changes['loading']
    ) {
      this.emitRendererState();
    }
  }

  ngAfterViewInit(): void {
    this.loadMoreSubject
      .pipe(debounceTime(250), takeUntil(this.destroySubject))
      .subscribe(() => this.requestNextPage());

    this.initIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
    this.destroySubject.next();
    this.destroySubject.complete();
    this.loadMoreSubject.complete();
  }

  toggleSelection(itemId: string): void {
    if (this.loading) {
      return;
    }

    const nextSelectedIds = new Set(this.selectedIds());
    if (nextSelectedIds.has(itemId)) {
      nextSelectedIds.delete(itemId);
    } else {
      nextSelectedIds.add(itemId);
    }

    this.selectedIds.set([...nextSelectedIds]);
    this.emitRendererState();
  }

  onLazyLoadVisible(): void {
    this.loadMoreSubject.next();
  }

  requestNextPage(): void {
    if (!this.canLoadMore || this.loading || this.lazyLoading) {
      return;
    }

    const page = this.currentPage + 1;
    this.pageRequested.emit({
      page,
      size: this.pageSize,
    });
    this.emitRendererState(page);
  }

  isSelected(itemId: string): boolean {
    return this.selectedIds().includes(itemId);
  }

  get currentPage(): number {
    return this.authInteraction.page?.page ?? this.initialDraftState?.page ?? 1;
  }

  get pageSize(): number {
    return this.authInteraction.page?.size ?? this.initialDraftState?.size ?? 25;
  }

  get totalPages(): number {
    return this.authInteraction.page?.total_pages ?? 1;
  }

  get totalItems(): number {
    return this.authInteraction.page?.total_items ?? 0;
  }

  get pageItems() {
    return this.loadedItems();
  }

  get canLoadMore(): boolean {
    return this.currentPage < this.totalPages;
  }

  private syncLoadedItems(): void {
    const page = this.authInteraction.page;
    const incomingItems = page?.items ?? [];

    if (!page || page.page <= 1 || this.loadedItems().length === 0) {
      this.loadedItems.set(incomingItems);
      return;
    }

    const itemsById = new Map(
      this.loadedItems().map((item) => [item.id, item]),
    );

    for (const item of incomingItems) {
      itemsById.set(item.id, item);
    }

    this.loadedItems.set([...itemsById.values()]);
  }

  private initIntersectionObserver(): void {
    this.disconnectObserver();

    if (
      !this.observerTarget ||
      typeof IntersectionObserver === 'undefined' ||
      !this.canLoadMore
    ) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.onLazyLoadVisible();
          }
        }
      },
      { threshold: 1.0 },
    );

    this.observer.observe(this.observerTarget.nativeElement);
  }

  private disconnectObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private emitRendererState(pageOverride?: number): void {
    this.rendererStateChange.emit({
      payload: {
        selected_ids: this.selectedIds(),
      },
      submitDisabled: this.selectedIds().length === 0 || this.loading,
      draftState: {
        selected_ids: this.selectedIds(),
        page: pageOverride ?? this.currentPage,
        size: this.pageSize,
      },
    });
  }
}
